import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { promemoriaApi, motoApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Bell, Plus, Trash2, Loader2, AlertTriangle, CheckCircle2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { differenceInDays } from 'date-fns';

const TIPI_PROMEMORIA = [
  'Assicurazione',
  'Bollo',
  'Revisione',
  'Tagliando',
  'Patente',
  'Gomme',
  'Filtri',
  'Altro'
];

export default function Promemoria() {
  const [promemoria, setPromemoria] = useState([]);
  const [motoList, setMotoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [formData, setFormData] = useState({
    moto_id: '',
    tipo: '',
    titolo: '',
    descrizione: '',
    data_scadenza: '',
    completato: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [promRes, motoRes] = await Promise.all([
        promemoriaApi.getAll(),
        motoApi.getAll()
      ]);
      setPromemoria(promRes.data);
      setMotoList(motoRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      moto_id: '',
      tipo: '',
      titolo: '',
      descrizione: '',
      data_scadenza: '',
      completato: false
    });
  };

  const handleOpenDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tipo || !formData.titolo || !formData.data_scadenza) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        moto_id: formData.moto_id || null
      };
      await promemoriaApi.create(dataToSend);
      toast.success('Promemoria creato');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleComplete = async (prom) => {
    try {
      await promemoriaApi.update(prom.id, { completato: !prom.completato });
      toast.success(prom.completato ? 'Promemoria riattivato' : 'Promemoria completato');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo promemoria?')) return;
    
    try {
      await promemoriaApi.delete(id);
      toast.success('Promemoria eliminato');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.includes('.')) {
      const [day, month, year] = dateStr.split('.');
      return new Date(year, month - 1, day);
    }
    return new Date(dateStr);
  };

  const getDaysUntil = (dateStr) => {
    const date = parseDate(dateStr);
    if (!date) return null;
    return differenceInDays(date, new Date());
  };

  const getMotoName = (motoId) => {
    if (!motoId) return null;
    const moto = motoList.find(m => m.id === motoId);
    return moto ? `${moto.marca} ${moto.modello}` : null;
  };

  const filteredPromemoria = promemoria.filter(p => showCompleted ? p.completato : !p.completato);
  
  // Sort by urgency (closest to due date first, overdue at top)
  const sortedPromemoria = [...filteredPromemoria].sort((a, b) => {
    const daysA = getDaysUntil(a.data_scadenza) || 9999;
    const daysB = getDaysUntil(b.data_scadenza) || 9999;
    return daysA - daysB;
  });

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container" data-testid="promemoria-page">
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Promemoria</h1>
            <p className="page-subtitle">Scadenze e appuntamenti</p>
          </div>
          
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-[#9CA3AF] text-sm cursor-pointer">
              <Checkbox
                checked={showCompleted}
                onCheckedChange={setShowCompleted}
                className="border-[#262626] data-[state=checked]:bg-[#FF5F1F] data-[state=checked]:border-[#FF5F1F]"
              />
              Mostra completati
            </label>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={handleOpenDialog}
                  className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm h-12 px-6"
                  data-testid="add-promemoria-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Nuovo
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-['Teko'] text-2xl text-white uppercase">
                    Nuovo Promemoria
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label className="form-label">Tipo *</Label>
                    <Select 
                      value={formData.tipo} 
                      onValueChange={(v) => setFormData({...formData, tipo: v})}
                    >
                      <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11" data-testid="prom-tipo-select">
                        <SelectValue placeholder="Tipo scadenza" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                        {TIPI_PROMEMORIA.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="form-label">Titolo *</Label>
                    <Input
                      value={formData.titolo}
                      onChange={(e) => setFormData({...formData, titolo: e.target.value})}
                      placeholder="es. Rinnovo assicurazione Ducati"
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                      data-testid="prom-titolo"
                    />
                  </div>

                  {motoList.length > 0 && (
                    <div>
                      <Label className="form-label">Moto (opzionale)</Label>
                      <Select 
                        value={formData.moto_id} 
                        onValueChange={(v) => setFormData({...formData, moto_id: v === '__none__' ? '' : v})}
                      >
                        <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11" data-testid="prom-moto-select">
                          <SelectValue placeholder="Generale" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                          <SelectItem value="__none__">Generale</SelectItem>
                          {motoList.map(m => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.marca} {m.modello}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label className="form-label">Data Scadenza *</Label>
                    <Input
                      value={formData.data_scadenza}
                      onChange={(e) => setFormData({...formData, data_scadenza: e.target.value})}
                      placeholder="GG.MM.AAAA"
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                      data-testid="prom-data"
                    />
                  </div>

                  <div>
                    <Label className="form-label">Descrizione (opzionale)</Label>
                    <Input
                      value={formData.descrizione}
                      onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                      placeholder="Note aggiuntive..."
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                      data-testid="prom-descrizione"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      className="flex-1 h-11 border-[#262626] text-white hover:bg-white/5 uppercase tracking-wider rounded-sm"
                    >
                      Annulla
                    </Button>
                    <Button
                      type="submit"
                      disabled={saving}
                      className="flex-1 h-11 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm"
                      data-testid="prom-submit"
                    >
                      {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Crea'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {sortedPromemoria.length === 0 ? (
          <div className="vault-card text-center py-16">
            <Bell className="w-16 h-16 text-[#4B5563] mx-auto mb-4" />
            <h3 className="text-white text-xl mb-2">
              {showCompleted ? 'Nessun promemoria completato' : 'Nessun promemoria attivo'}
            </h3>
            <p className="text-[#9CA3AF] mb-6">
              {showCompleted ? 'I promemoria completati appariranno qui' : 'Aggiungi scadenze e appuntamenti'}
            </p>
            {!showCompleted && (
              <Button 
                onClick={handleOpenDialog}
                className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm h-11 px-6"
              >
                <Plus className="w-5 h-5 mr-2" />
                Nuovo Promemoria
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {sortedPromemoria.map((prom) => {
              const daysUntil = getDaysUntil(prom.data_scadenza);
              const isUrgent = daysUntil !== null && daysUntil <= 7 && daysUntil >= 0;
              const isOverdue = daysUntil !== null && daysUntil < 0;
              const motoName = getMotoName(prom.moto_id);
              
              return (
                <div
                  key={prom.id}
                  className={`vault-card flex flex-col sm:flex-row sm:items-center gap-4 ${
                    prom.completato 
                      ? 'opacity-60' 
                      : isOverdue 
                        ? 'border-red-500/50' 
                        : isUrgent 
                          ? 'border-yellow-500/50' 
                          : ''
                  }`}
                  data-testid={`promemoria-item-${prom.id}`}
                >
                  <button
                    onClick={() => handleToggleComplete(prom)}
                    className={`w-10 h-10 flex items-center justify-center flex-shrink-0 transition-colors ${
                      prom.completato 
                        ? 'bg-green-500/20' 
                        : isOverdue 
                          ? 'bg-red-500/10' 
                          : isUrgent 
                            ? 'bg-yellow-500/10' 
                            : 'bg-[#FF5F1F]/10'
                    }`}
                    data-testid={`toggle-prom-${prom.id}`}
                  >
                    {prom.completato ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : isOverdue ? (
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    ) : isUrgent ? (
                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    ) : (
                      <Bell className="w-5 h-5 text-[#FF5F1F]" />
                    )}
                  </button>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className={`vault-badge ${
                        prom.completato 
                          ? 'vault-badge-green' 
                          : isOverdue 
                            ? 'vault-badge-red' 
                            : 'vault-badge-orange'
                      }`}>
                        {prom.tipo}
                      </span>
                      {motoName && (
                        <span className="text-[#9CA3AF] text-xs">{motoName}</span>
                      )}
                    </div>
                    <h3 className={`font-medium ${prom.completato ? 'text-[#9CA3AF] line-through' : 'text-white'}`}>
                      {prom.titolo}
                    </h3>
                    {prom.descrizione && (
                      <p className="text-[#9CA3AF] text-sm mt-1">{prom.descrizione}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <Calendar className="w-4 h-4 text-[#9CA3AF]" />
                        <p className={`font-mono ${
                          prom.completato 
                            ? 'text-[#9CA3AF]' 
                            : isOverdue 
                              ? 'text-red-500' 
                              : isUrgent 
                                ? 'text-yellow-500' 
                                : 'text-[#FF5F1F]'
                        }`}>
                          {prom.data_scadenza}
                        </p>
                      </div>
                      {!prom.completato && daysUntil !== null && (
                        <p className={`text-xs mt-1 ${
                          isOverdue ? 'text-red-500' : isUrgent ? 'text-yellow-500' : 'text-[#9CA3AF]'
                        }`}>
                          {isOverdue 
                            ? `Scaduto da ${Math.abs(daysUntil)} giorni`
                            : daysUntil === 0 
                              ? 'Scade oggi'
                              : `Tra ${daysUntil} giorni`
                          }
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDelete(prom.id)}
                      className="p-2 text-[#9CA3AF] hover:text-red-500 transition-colors"
                      data-testid={`delete-prom-${prom.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
